import React, { useMemo, useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Table, 
  Space, 
  Modal, 
  Select,
  InputNumber,
  Switch,
  Tag,
  message,
  Row,
  Col,
  TimePicker,
  DatePicker,
  Grid
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import EntityCardList from './EntityCardList';

const { TextArea } = Input;
const { Option } = Select;

const SessionManagement = ({ sessions, setSessions, speakers = [] }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [form] = Form.useForm();
  const isMobile = Grid.useBreakpoint().md === false;

  const speakerOptions = useMemo(() => {
    return (speakers || [])
      .map((speaker) => {
        const speakerId = speaker?.id || speaker?.Id;
        if (!speakerId) {
          return null;
        }

        const speakerName = speaker?.name || speaker?.fullName || speaker?.Name || t("eventSections.sessions.unnamedSpeaker");
        const speakerTitle = speaker?.title || speaker?.Title;

        return {
          id: speakerId,
          label: speakerTitle ? `${speakerName} - ${speakerTitle}` : speakerName,
        };
      })
      .filter(Boolean);
  }, [speakers, t]);

  const handleAddSession = () => {
    setEditingSession(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    form.setFieldsValue({
      ...session,
      sessionDate: session.sessionDate ? moment(session.sessionDate) : null,
      startTime: session.startTime ? moment(session.startTime, 'HH:mm A') : null,
      endTime: session.endTime ? moment(session.endTime, 'HH:mm A') : null,
    });
    setModalVisible(true);
  };

  const handleDeleteSession = (sessionId) => {
    Modal.confirm({
      title: t('eventSections.sessions.delete'),
      content: t('eventSections.sessions.deleteConfirm'),
      okText: t('eventSections.common.delete'),
      okType: 'danger',
      onOk: () => {
        setSessions(sessions.filter(s => s.id !== sessionId));
        message.success(t('eventSections.sessions.deleted'));
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const sessionData = {
        id: editingSession?.id || crypto.randomUUID(),
        title: values.title,
        description: values.description,
        sessionDate: values.sessionDate ? values.sessionDate.format('YYYY-MM-DD') : null,
        startTime: values.startTime ? values.startTime.format('hh:mm A') : null,
        endTime: values.endTime ? values.endTime.format('hh:mm A') : null,
        duration: values.duration,
        dayNumber: values.dayNumber,
        sessionType: values.sessionType,
        track: values.track,
        location: values.location,
        capacity: values.capacity,
        speakerIds: values.speakerIds || [],
        isBreak: values.isBreak || false,
        requiresRegistration: values.requiresRegistration || false,
        tags: values.tags || [],
        arabicTitle: values.arabicTitle,
        arabicDescription: values.arabicDescription,
        arabicLocation: values.arabicLocation,
        order: values.order || sessions.length
      };

      if (editingSession) {
        setSessions(sessions.map(s => s.id === editingSession.id ? sessionData : s));
        message.success(t('eventSections.sessions.updated'));
      } else {
        setSessions([...sessions, sessionData]);
        message.success(t('eventSections.sessions.added'));
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const columns = [
    {
      title: t('eventSections.common.order'),
      dataIndex: 'order',
      width: 70,
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: t('eventSections.sessions.columns.dateDay'),
      key: 'date',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.sessionDate && <div className="text-sm">{moment(record.sessionDate).format('MMM DD')}</div>}
          {record.dayNumber && <Tag color="blue">{t('eventSections.sessions.day', { day: record.dayNumber })}</Tag>}
        </Space>
      ),
      sorter: (a, b) => {
        const dateA = a.sessionDate ? new Date(a.sessionDate).getTime() : 0;
        const dateB = b.sessionDate ? new Date(b.sessionDate).getTime() : 0;
        return dateA - dateB;
      }
    },
    {
      title: t('eventSections.sessions.columns.title'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          {record.isBreak && <Tag color="orange">{t('eventSections.sessions.break')}</Tag>}
          {record.sessionType && <Tag color="blue">{t(`eventSections.sessions.types.${record.sessionType}`, { defaultValue: record.sessionType })}</Tag>}
        </div>
      )
    },
    {
      title: t('eventSections.sessions.columns.time'),
      key: 'time',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <div><ClockCircleOutlined /> {record.startTime} - {record.endTime}</div>
          {record.duration && <div className="text-gray-500 text-xs">{t('eventSections.sessions.minutes', { count: record.duration })}</div>}
        </Space>
      )
    },
    {
      title: t('eventSections.sessions.columns.location'),
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: t('eventSections.sessions.columns.speakers'),
      dataIndex: 'speakerIds',
      key: 'speakers',
      render: (speakerIds) => (
        <div>
          <UserOutlined /> {t('eventSections.sessions.speakerCount', { count: speakerIds?.length || 0 })}
        </div>
      )
    },
    {
      title: t('eventSections.sessions.columns.track'),
      dataIndex: 'track',
      key: 'track',
      render: (track) => track && <Tag>{track}</Tag>
    },
    {
      title: t('eventSections.common.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditSession(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteSession(record.id)}
          />
        </Space>
      )
    }
  ];

  return (
    <Card 
      title={t('eventSections.sessions.title')}
      extra={
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAddSession}
        >
          {t('eventSections.sessions.add')}
        </Button>
      }
    >
      {isMobile ? (
        <EntityCardList
          data={[...sessions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))}
          avatar={() => <ClockCircleOutlined style={{ fontSize: 22, color: '#1890ff', flexShrink: 0 }} />}
          title={(r) => r.title}
          subtitle={(r) => [
            r.sessionDate && moment(r.sessionDate).format('MMM DD'),
            r.startTime && r.endTime && `${r.startTime} - ${r.endTime}`,
          ].filter(Boolean).join(' · ')}
          tags={(r) => (
            <>
              {r.isBreak && <Tag color="orange">{t('eventSections.sessions.break')}</Tag>}
              {r.dayNumber && <Tag color="blue">{t('eventSections.sessions.day', { day: r.dayNumber })}</Tag>}
              {r.sessionType && <Tag color="blue">{t(`eventSections.sessions.types.${r.sessionType}`, { defaultValue: r.sessionType })}</Tag>}
              {r.track && <Tag>{r.track}</Tag>}
            </>
          )}
          details={(r) => (
            <>
              {r.location && <span>{r.location}</span>}
              <span><UserOutlined /> {t('eventSections.sessions.speakerCount', { count: r.speakerIds?.length || 0 })}</span>
            </>
          )}
          menuItems={(r) => [
            { key: 'edit', icon: <EditOutlined />, label: t('eventSections.sessions.edit'), onClick: () => handleEditSession(r) },
            { key: 'delete', icon: <DeleteOutlined />, label: t('eventSections.sessions.delete'), danger: true, onClick: () => handleDeleteSession(r.id) },
          ]}
          onTap={handleEditSession}
          emptyText={t('eventSections.sessions.empty')}
        />
      ) : (
        <Table
          dataSource={sessions}
          columns={columns}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('eventSections.sessions.empty') }}
        />
      )}

      <Modal
        title={editingSession ? t('eventSections.sessions.edit') : t('eventSections.sessions.add')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={isMobile ? 'calc(100vw - 16px)' : 800}
        style={isMobile ? { top: 12 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' } } : undefined}
        okText={editingSession ? t('eventSections.common.update') : t('eventSections.common.add')}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={18}>
              <Form.Item
                name="title"
                label={t('eventSections.sessions.form.sessionTitle')}
                rules={[{ required: true, message: t('eventSections.sessions.form.titleRequired') }]}
              >
                <Input placeholder={t('eventSections.sessions.form.titlePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="order"
                label={t('eventSections.common.order')}
                initialValue={sessions.length}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label={t('eventSections.sessions.form.description')}
          >
            <TextArea rows={3} placeholder={t('eventSections.sessions.form.descriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sessionDate"
                label={t('eventSections.sessions.form.sessionDate')}
                help={t('eventSections.sessions.form.sessionDateHelp')}
              >
                <DatePicker 
                  format="YYYY-MM-DD" 
                  style={{ width: '100%' }}
                  placeholder={t('eventSections.sessions.form.selectDate')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dayNumber"
                label={t('eventSections.sessions.form.dayNumber')}
                help={t('eventSections.sessions.form.dayNumberHelp')}
              >
                <InputNumber min={1} max={365} style={{ width: '100%' }} placeholder={t('eventSections.sessions.form.dayPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="startTime"
                label={t('eventSections.sessions.form.startTime')}
              >
                <TimePicker format="hh:mm A" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="endTime"
                label={t('eventSections.sessions.form.endTime')}
              >
                <TimePicker format="hh:mm A" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="duration"
                label={t('eventSections.sessions.form.durationMinutes')}
              >
                <InputNumber min={1} max={1440} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sessionType"
                label={t('eventSections.sessions.form.sessionType')}
              >
                <Select placeholder={t('eventSections.sessions.form.selectType')}>
                  <Option value="keynote">{t('eventSections.sessions.types.keynote')}</Option>
                  <Option value="workshop">{t('eventSections.sessions.types.workshop')}</Option>
                  <Option value="panel">{t('eventSections.sessions.types.panel')}</Option>
                  <Option value="breakout">{t('eventSections.sessions.types.breakout')}</Option>
                  <Option value="networking">{t('eventSections.sessions.types.networking')}</Option>
                  <Option value="presentation">{t('eventSections.sessions.types.presentation')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="track"
                label={t('eventSections.sessions.form.track')}
              >
                <Input placeholder={t('eventSections.sessions.form.trackPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="location"
                label={t('eventSections.sessions.form.location')}
              >
                <Input placeholder={t('eventSections.sessions.form.locationPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="capacity"
                label={t('eventSections.sessions.form.capacity')}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder={t('eventSections.sessions.form.capacityPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="speakerIds"
            label={t('eventSections.sessions.form.speakers')}
          >
            <Select
              key={`session-speakers-${speakerOptions.length}`}
              mode="multiple"
              placeholder={t('eventSections.sessions.form.speakersPlaceholder')}
              optionFilterProp="children"
            >
              {speakerOptions.map((speaker) => (
                <Option key={speaker.id} value={speaker.id}>
                  {speaker.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="tags"
            label={t('eventSections.sessions.form.tags')}
          >
            <Select
              mode="tags"
              placeholder={t('eventSections.sessions.form.tagsPlaceholder')}
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isBreak"
                label={t('eventSections.sessions.form.isBreak')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            {/* <Col span={12}>
              <Form.Item
                name="requiresRegistration"
                label="Requires Registration"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col> */}
          </Row>

          <Card title={t('eventSections.common.arabicTranslation')} size="small" style={{ marginTop: 16 }}>
            <Form.Item
              name="arabicTitle"
              label={t('eventSections.common.arabicTitle')}
            >
              <Input placeholder="العنوان بالعربية" style={{ direction: 'rtl' }} />
            </Form.Item>
            <Form.Item
              name="arabicDescription"
              label={t('eventSections.common.arabicDescription')}
            >
              <TextArea rows={2} placeholder="الوصف بالعربية" style={{ direction: 'rtl' }} />
            </Form.Item>
            <Form.Item
              name="arabicLocation"
              label={t('eventSections.sessions.form.arabicLocation')}
            >
              <Input placeholder="الموقع بالعربية" style={{ direction: 'rtl' }} />
            </Form.Item>
          </Card>
        </Form>
      </Modal>
    </Card>
  );
};

export default SessionManagement;
